import { useCallback, useEffect, useRef, useState } from 'react';
import { getUserList } from '../../../../../services/users';
import { getDepartmentList } from '../../../../../services/departments';
import { shareDashboard } from '../../../../../services/dashboardBuilder';
import {
  ASSIGNEE_TYPES,
  isShareSearchMocksEnabled,
  mergeShareSearchMockDepartments,
  PERMISSION_TYPES,
  VIEW_PERMISSIONS,
} from '../../constants';
import { assignmentToSharedWithEntry, normalizeViewPermissions } from '../../utils/permissionsHelpers';

const INITIAL_RESULTS = {
  users: [],
  departments: [],
};

const getUserId = (user) => user?.userUuid || user?.user_uuid || user?.uuid || user?.id || null;

const getDepartmentId = (department) =>
  department?.departmentUuid || department?.department_uuid || department?.uuid || department?.id || null;

const mapUserResult = (user) => ({
  id: getUserId(user),
  name: user?.fullName || user?.full_name || user?.name || 'Unknown user',
  email: user?.email || '',
  role: user?.roleName || user?.role_name || user?.jobTitle || user?.job_title || '',
  type: ASSIGNEE_TYPES.USER,
});

const mapDepartmentResult = (department) => ({
  id: getDepartmentId(department),
  name: department?.departmentName || department?.department_name || department?.name || 'Unknown department',
  userCount: department?.userCount || department?.total_active_members || 0,
  type: ASSIGNEE_TYPES.DEPARTMENT,
});

/**
 * Filtra departamentos por keyword (necesario porque mergeShareSearchMockDepartments
 * anexa mocks sin filtrar). Usuarios ya vienen filtrados del API.
 */
const filterDepartmentsByKeyword = (departments, keyword) => {
  const k = String(keyword ?? '').trim().toLowerCase();
  if (!k) return [];
  const matches = (text) => String(text ?? '').toLowerCase().includes(k);
  return (departments || []).filter((d) => matches(d.name));
};

/** Normaliza distintas formas de lista que devuelven user/list y department/list */
const normalizeListPayload = (payload) => {
  if (payload == null) return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload.body)) return payload.body;
  if (payload.body && typeof payload.body === 'object') {
    if (Array.isArray(payload.body.users)) return payload.body.users;
    if (Array.isArray(payload.body.items)) return payload.body.items;
    if (Array.isArray(payload.body.data)) return payload.body.data;
  }
  if (Array.isArray(payload.users)) return payload.users;
  if (Array.isArray(payload.items)) return payload.items;
  if (Array.isArray(payload.data)) return payload.data;
  return [];
};

export const useShareDashboard = ({ enabled = true } = {}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState(INITIAL_RESULTS);
  const [searchLoading, setSearchLoading] = useState(false);
  const [stagedSelections, setStagedSelections] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const searchTimeoutRef = useRef(null);
  const requestIdRef = useRef(0);
  const assignmentsRef = useRef(assignments);
  assignmentsRef.current = assignments;
  const stagedRef = useRef(stagedSelections);
  stagedRef.current = stagedSelections;

  const clearSearch = useCallback(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = null;
    }
    requestIdRef.current += 1;
    setSearchTerm('');
    setSearchLoading(false);
    setSearchResults(INITIAL_RESULTS);
  }, []);

  const searchUsersAndDepartments = useCallback(
    async (keyword) => {
      if (!enabled) {
        return;
      }

      const trimmedKeyword = keyword?.trim();
      if (!trimmedKeyword) {
        setSearchLoading(false);
        setSearchResults(INITIAL_RESULTS);
        return;
      }

      const requestId = ++requestIdRef.current;
      setSearchLoading(true);

      try {
        const [usersSettled, departmentsSettled] = await Promise.allSettled([
          getUserList({ search: trimmedKeyword, page_size: 5 }),
          getDepartmentList({ search: { keyword: trimmedKeyword }, page_size: 5 }),
        ]);

        if (requestId !== requestIdRef.current) return;

        if (usersSettled.status === 'rejected') {
          console.warn('Share search: getUserList failed', usersSettled.reason);
        }
        if (departmentsSettled.status === 'rejected') {
          console.warn('Share search: getDepartmentList failed', departmentsSettled.reason);
        }

        const rawUsers =
          usersSettled.status === 'fulfilled'
            ? normalizeListPayload(usersSettled.value)
            : [];
        const rawDepartments =
          departmentsSettled.status === 'fulfilled'
            ? normalizeListPayload(departmentsSettled.value)
            : [];

        const users = rawUsers.map(mapUserResult).filter((u) => u.id != null);
        const departmentsFromApi = rawDepartments
          .map(mapDepartmentResult)
          .filter((d) => d.id != null);

        const departmentsMerged = mergeShareSearchMockDepartments(departmentsFromApi);
        const departments = filterDepartmentsByKeyword(departmentsMerged, trimmedKeyword);

        setSearchResults({ users, departments });
      } catch (error) {
        console.error('Error searching users and departments:', error);
        if (requestId !== requestIdRef.current) return;
        if (isShareSearchMocksEnabled()) {
          const mockDepts = mergeShareSearchMockDepartments([]);
          setSearchResults({
            users: [],
            departments: filterDepartmentsByKeyword(mockDepts, trimmedKeyword),
          });
        } else {
          setSearchResults(INITIAL_RESULTS);
        }
      } finally {
        if (requestId !== requestIdRef.current) return;
        setSearchLoading(false);
      }
    },
    [enabled]
  );

  const onSearchChange = useCallback(
    (nextSearchTerm) => {
      setSearchTerm(nextSearchTerm);

      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
        searchTimeoutRef.current = null;
      }

      const trimmedKeyword = nextSearchTerm?.trim();
      if (!trimmedKeyword || !enabled) {
        requestIdRef.current += 1;
        setSearchLoading(false);
        setSearchResults(INITIAL_RESULTS);
        return;
      }

      searchTimeoutRef.current = setTimeout(() => {
        searchUsersAndDepartments(trimmedKeyword);
      }, 500);
    },
    [enabled, searchUsersAndDepartments]
  );

  const addStagedSelection = useCallback((item) => {
    if (!item?.id) return;
    setStagedSelections((prev) => {
      if (prev.some((entry) => entry.id === item.id)) return prev;
      return [...prev, item];
    });
  }, []);

  const removeStagedSelection = useCallback((id) => {
    setStagedSelections((prev) => prev.filter((entry) => entry.id !== id));
  }, []);

  const commitStaged = useCallback(() => {
    const current = stagedRef.current;
    if (!current.length) return;
    setAssignments((prev) => {
      const existingIds = new Set(prev.map((entry) => entry.id));
      const next = [...prev];
      current.forEach((item) => {
        if (!item?.id || existingIds.has(item.id)) return;
        existingIds.add(item.id);
        next.push({
          ...item,
          permissionLevel: PERMISSION_TYPES.VIEW,
          permissionModes: [PERMISSION_TYPES.VIEW],
          editPermissions: [],
          viewPermissions: [VIEW_PERMISSIONS.VIEW_ONLY],
        });
      });
      return next;
    });
    setStagedSelections([]);
    clearSearch();
  }, [clearSearch]);

  const removeAssignment = useCallback((id) => {
    setAssignments((prev) => prev.filter((entry) => entry.id !== id));
  }, []);

  const updatePermissions = useCallback((id, payload) => {
    setAssignments((prev) =>
      prev.map((entry) => {
        if (entry.id !== id) return entry;
        const permissionModes = Array.isArray(payload?.permissionModes)
          ? payload.permissionModes.filter(
              (mode) =>
                mode === PERMISSION_TYPES.VIEW ||
                mode === PERMISSION_TYPES.EDIT ||
                mode === PERMISSION_TYPES.CREATE
            )
          : Array.isArray(entry.permissionModes)
            ? entry.permissionModes
            : [entry.permissionLevel || PERMISSION_TYPES.VIEW];
        const resolvedModes =
          permissionModes.length > 0 ? Array.from(new Set(permissionModes)) : [PERMISSION_TYPES.VIEW];
        const permissionLevel = resolvedModes.includes(PERMISSION_TYPES.EDIT)
          ? PERMISSION_TYPES.EDIT
          : PERMISSION_TYPES.VIEW;
        const editPermissions =
          !resolvedModes.includes(PERMISSION_TYPES.EDIT)
            ? []
            : Array.isArray(payload?.editPermissions)
              ? payload.editPermissions
              : entry.editPermissions || [];
        const rawViewPermissions = Array.isArray(payload?.viewPermissions)
          ? payload.viewPermissions
          : Array.isArray(entry.viewPermissions)
            ? entry.viewPermissions
            : [VIEW_PERMISSIONS.VIEW_ONLY];
        const viewPermissions = normalizeViewPermissions(rawViewPermissions);
        return { ...entry, permissionLevel, permissionModes: resolvedModes, editPermissions, viewPermissions };
      })
    );
  }, []);

  const confirmShare = useCallback(async (dashboardId) => {
    try {
      const sharedEntries = assignmentsRef.current.map(assignmentToSharedWithEntry);
      if (dashboardId && dashboardId !== 'new') {
        await shareDashboard(dashboardId, sharedEntries);
      }
      setShowConfirmation(false);
      return { success: true, sharedEntries };
    } catch (error) {
      console.error('Error sharing dashboard:', error);
      return { success: false, error };
    }
  }, []);

  const resetShareState = useCallback(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = null;
    }
    requestIdRef.current += 1;
    setSearchTerm('');
    setSearchLoading(false);
    setSearchResults(INITIAL_RESULTS);
    setStagedSelections([]);
    setAssignments([]);
    setShowConfirmation(false);
  }, []);

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      requestIdRef.current += 1;
    };
  }, []);

  return {
    searchTerm,
    setSearchTerm,
    searchResults,
    searchLoading,
    onSearchChange,
    clearSearch,
    stagedSelections,
    addStagedSelection,
    removeStagedSelection,
    commitStaged,
    assignments,
    removeAssignment,
    updatePermissions,
    showConfirmation,
    setShowConfirmation,
    confirmShare,
    resetShareState,
  };
};
